import { isClarityOneHostname, isClarityOneCustomerPath } from "@shared/clarityOneDomain";
import { clarityOneLoginUrlWithRedirect, sanitizeClarityOneRedirect } from "@shared/clarityOneAuthRedirect";

function readDevFlag(): boolean {
  try {
    return import.meta.env.VITE_CLARITY_ONE_DEV === "true";
  } catch {
    return false;
  }
}

/** True when VITE_CLARITY_ONE_DEV is set (local dev tooling). */
export function isClarityOneLocalDevEnabled(): boolean {
  if (typeof window === "undefined") return readDevFlag() && import.meta.env.DEV === true;
  return import.meta.env.DEV === true && readDevFlag();
}

/**
 * True when the SPA should render Clarity One routes:
 * - production hostname one.claritygroup.com.sg (all paths), or
 * - localhost dev with flag on AND current path is a customer path.
 */
export function isClarityOneMode(): boolean {
  if (typeof window === "undefined") return false;
  return shouldUseClarityOneRouter(window.location.hostname, window.location.pathname);
}

/** Staff login path when Clarity One occupies /login locally. */
export function getStaffLoginPath(): string {
  return isClarityOneLocalDevEnabled() ? "/staff-login" : "/login";
}

export function getClarityOneLoginUrl(targetPath?: string): string {
  const dest = targetPath ?? (typeof window !== "undefined" ? window.location.pathname : "/");
  return clarityOneLoginUrlWithRedirect(sanitizeClarityOneRedirect(dest));
}

/** Human-readable hint when dev flag is off but user hits Clarity One paths locally. */
export function clarityOneLocalDevHint(): string | null {
  if (typeof window === "undefined") return null;
  if (isClarityOneHostname(window.location.hostname)) return null;
  if (isClarityOneLocalDevEnabled()) return null;
  if (!isClarityOneCustomerPath(window.location.pathname)) return null;
  return "Clarity One local mode is off. Set VITE_CLARITY_ONE_DEV=true in .env and restart the dev server.";
}

type ClarityOneRoutingEnv = {
  isViteDev: boolean;
  clarityOneDevFlag: boolean;
};

function resolveClarityOneRoutingEnv(overrides?: Partial<ClarityOneRoutingEnv>): ClarityOneRoutingEnv {
  return {
    isViteDev: overrides?.isViteDev ?? import.meta.env.DEV === true,
    clarityOneDevFlag: overrides?.clarityOneDevFlag ?? readDevFlag(),
  };
}

/**
 * True when the SPA should show the localhost-only Clarity One dev fallback screen.
 * Never true in production builds (import.meta.env.DEV is false).
 */
export function shouldShowClarityOneLocalDevFallback(
  hostname: string,
  pathname: string,
  overrides?: Partial<ClarityOneRoutingEnv>,
): boolean {
  const { isViteDev, clarityOneDevFlag } = resolveClarityOneRoutingEnv(overrides);
  const onClarityHost = isClarityOneHostname(hostname);
  // Share folder is anonymous on both portals — never block it behind the local-dev hint.
  // Public VO Link B is also registered on the staff App router for local verification.
  const pathOnly = pathname.split("?")[0] ?? pathname;
  if (pathOnly.startsWith("/share/")) return false;
  if (pathOnly.startsWith("/vo/")) return false;
  const onClarityPath = isClarityOneCustomerPath(pathname);
  const localDevEnabled = isViteDev && clarityOneDevFlag;
  return onClarityPath && !onClarityHost && isViteDev && !localDevEnabled;
}

/** True when the SPA should mount ClarityOneDomainRouter instead of staff routes. */
export function shouldUseClarityOneRouter(
  hostname: string,
  pathname: string,
  overrides?: Partial<ClarityOneRoutingEnv>,
): boolean {
  if (isClarityOneHostname(hostname)) return true;
  const { isViteDev, clarityOneDevFlag } = resolveClarityOneRoutingEnv(overrides);
  if (!isViteDev || !clarityOneDevFlag) return false;
  return isClarityOneCustomerPath(pathname);
}
